import { Card, Input } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { SaveGuardModal } from 'features/libra/SaveGuardModal'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useRegisterSaveGuard } from 'features/libra/useRegisterSaveGuard'
import {
  LibraWorflowStepComponentProps,
  StepValidationResult,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import { Layers } from 'lucide-react'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'

import {
  NiiSaveBanner,
  StickySaveBannerContainer,
} from './niiReport/NiiSaveBanner'
import { ZoneAnalysisReviewPanel } from './niiReview/ZoneAnalysisReviewPanel'
import { ConsequenceOfFailureSection } from './zoneAnalysis/components/ConsequenceOfFailureSection'
import { NiiZones } from './zoneAnalysis/components/NiiZones'
import {
  ZONE_FIELD_IDS,
  zoneAnalysisConfigSchema,
} from './zoneAnalysis/zoneAnalysisApi'
import {
  useZoneAnalysisData,
  ZoneAnalysisDataProvider,
} from './zoneAnalysis/ZoneAnalysisDataProvider'

const { TextArea } = Input

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const SectionCard = styled(Card)`
  margin-bottom: 0;
  border: 2px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.forecast.background1};

  .ant-card-head {
    background: ${({ theme }) => theme.forecast.background2};
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }

  .ant-card-head-title {
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const HelpText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-style: italic;
`

const ZoneAnalysisContent: FC<{
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
  readonly?: boolean
}> = ({ readonly }) => {
  const stepId = useActiveStepId()
  const {
    hasChanges,
    resetChanges,
    doSave,
    isLoading,
    cof,
    setCof,
    zoneBreakdownJustification,
    setZoneBreakdownJustification,
  } = useZoneAnalysisData()

  const saveGuard = useRegisterSaveGuard(!readonly && hasChanges, doSave)

  return (
    <Container>
      <SaveGuardModal {...saveGuard} />
      {!readonly && (
        <StickySaveBannerContainer>
          <NiiSaveBanner
            hasChanges={hasChanges}
            isLoading={isLoading}
            onReset={resetChanges}
            onSave={doSave}
          />
        </StickySaveBannerContainer>
      )}
      <CommentIndicator anchor={{ type: 'cof', stepId }}>
        <ConsequenceOfFailureSection
          cof={cof}
          onChange={setCof}
          readonly={readonly}
        />
      </CommentIndicator>
      <CommentIndicator
        anchor={{
          type: 'field',
          stepId,
          fieldId: ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION,
        }}>
        <SectionCard
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Layers size={18} />
              Zone Breakdown Justification
            </span>
          }>
          <FieldGroup>
            <HelpText>
              Explain why the asset was divided into zones in this way — e.g.,
              differences in material, geometry, damage mechanisms, operating
              environment, or inspection access between areas.
            </HelpText>
            <TextArea
              value={zoneBreakdownJustification ?? ''}
              onChange={(e) =>
                setZoneBreakdownJustification(e.target.value || undefined)
              }
              disabled={readonly}
              placeholder='e.g., The vessel was split into three zones to reflect distinct operating environments: the bottom zone experiences liquid phase contact with higher corrosion rates, the shell zone is in vapour phase, and the top head has separate access constraints requiring different inspection methods.'
              rows={4}
            />
          </FieldGroup>
        </SectionCard>
      </CommentIndicator>
      <NiiZones />
    </Container>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => zoneAnalysisConfigSchema.safeParse(config).success,
    [config]
  )
}

export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, unknown>>
> = ({ config, setValidationResult, readonly }) => {
  if (readonly) {
    return <ZoneAnalysisReviewPanel config={config} />
  }

  const parsedConfig = zoneAnalysisConfigSchema.parse(config)
  return (
    <ZoneAnalysisDataProvider config={parsedConfig}>
      <ZoneAnalysisContent
        setValidationResult={setValidationResult}
        readonly={readonly}
      />
    </ZoneAnalysisDataProvider>
  )
}
